import { RefreshCw, Mail, X } from 'lucide-react'
import {
  DataTable,
  type DataTableColumn,
  StatusBadge,
  Button,
  EmptyState,
} from '@atrvd/saas-ui'
import { type InvitationStatus, type OrganizationInvitation } from '@/hooks/useOrganizationInvitations'
import { formatDate } from '@dcp/shared/utils/formatters'

/**
 * Props for the InvitationsTable component
 */
interface InvitationsTableProps {
  /** Array of invitations to display */
  invitations: OrganizationInvitation[]
  /** Callback when resend button is clicked */
  onResend: (id: string) => void
  /** ID of invitation currently being resent (for per-item loading state) */
  resendingId?: string | null
  /** Optional callback when cancel button is clicked */
  onCancel?: (id: string) => void
  /** ID of invitation currently being cancelled (for per-item loading state) */
  cancellingId?: string | null
}

/**
 * Table component to display organization invitations
 * Shows invitation details with status badges and resend action for expired invitations
 */
export function InvitationsTable({
  invitations,
  onResend,
  resendingId = null,
  onCancel,
  cancellingId = null,
}: InvitationsTableProps) {
  /**
   * Column definitions for the invitations table
   * Defines structure and rendering for each column
   */
  const columns: DataTableColumn<OrganizationInvitation>[] = [
    {
      key: 'organization_name',
      header: 'Organization Name',
      cellClassName: 'font-medium',
    },
    {
      key: 'admin_email',
      header: 'Admin Email',
      render: (inv) => {
        const hasName = inv.admin_first_name || inv.admin_last_name
        return (
          <div className="flex flex-col">
            <span>{inv.admin_email}</span>
            {hasName && (
              <span className="text-xs text-muted-foreground">
                {inv.admin_first_name} {inv.admin_last_name}
              </span>
            )}
          </div>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (inv) => {
        // 'processing' is the transient mid-acceptance claim state. Rendered
        // as a neutral 'info' badge — same as 'open' — because for the admin
        // it just means "someone is currently accepting / has the form open".
        const statusMap: Record<InvitationStatus, 'info' | 'success' | 'error' | 'warning'> = {
          open: 'info',
          processing: 'info',
          accepted: 'success',
          expired: 'error',
          cancelled: 'warning',
        }
        return (
          <StatusBadge
            status={statusMap[inv.status]}
            label={inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
            showIcon
          />
        )
      },
    },
    {
      key: 'expires_at',
      header: 'Expires At',
      render: (inv) => formatDate(inv.expires_at),
    },
    {
      key: 'created_at',
      header: 'Created At',
      render: (inv) => formatDate(inv.created_at),
    },
    {
      key: 'id',
      header: 'Actions',
      render: (inv) => {
        const isThisResending = resendingId === inv.id
        const isThisCancelling = cancellingId === inv.id

        // Show resend button for expired invitations
        if (inv.status === 'expired') {
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResend(inv.id)}
              disabled={isThisResending}
            >
              {isThisResending ? (
                <RefreshCw className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="mr-2 h-3 w-3" />
                  Resend
                </>
              )}
            </Button>
          )
        }

        // Show cancel button for open invitations if onCancel is provided
        if (inv.status === 'open' && onCancel) {
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancel(inv.id)}
              disabled={isThisCancelling}
            >
              {isThisCancelling ? (
                <X className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <X className="mr-2 h-3 w-3" />
                  Cancel
                </>
              )}
            </Button>
          )
        }

        return null
      },
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={invitations}
      rowKey={(inv) => inv.id}
      emptyState={
        <EmptyState
          icon={Mail}
          title="No invitations found"
          description="Organization invitations will appear here"
        />
      }
    />
  )
}